"use client";

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  getTextronBalanceSheetData,
  getStructuredBalanceSheetData,
  getCompanyName,
  calculateDebitCreditMetrics,
  getDebitCreditBreakdown,
} from "../lib/textron-data-processor";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  ArrowUpDown,
  Calculator,
  Download,
  Save,
  Undo2,
  ToggleLeft,
  ToggleRight,
  Eye,
  DollarSign,
  Building,
  CreditCard,
  Loader2,
  FileText,
  Upload,
  Calendar,
  ChevronDown,
  ChevronUp,
  XCircle,
  Filter,
  Plus,
  X,
} from "lucide-react";
import {
  getMappingViewWithoutAmounts,
  getMappingViewWithAmounts,
  formatCurrency,
  type MappingViewWithoutAmounts,
  type MappingViewWithAmounts,
} from "../lib/textron-data-processor";
import React from "react";

/** Web archive mode disables shadcn `Button` unless this is set — see `components/ui/button.tsx`. */
const DATA_ALLOW_ARCHIVAL_EDIT = { "data-allow-archival-edit": true } as const;

interface TrialBalanceSectionProps {
  searchTerm: string;
}

// Memoized Note Line Dropdown Component using custom Select for better dark theme control
const NoteLineDropdown = React.memo<{
  value: number;
  onChange: (noteLineId: number) => void;
  options: Array<{ note_line_id: number; note_line: string }>;
  currentNoteLine: string;
}>(({ value, onChange, options, currentNoteLine }) => {
  // ️ CRITICAL: Track open state so SelectItem nodes are only created when the
  // dropdown is actually open.  With 1667 rows, rendering all options eagerly
  // for every row (even when closed) instantiates 1667 × N React fiber nodes
  // in memory simultaneously and causes the V8 OOM crash.
  const [open, setOpen] = React.useState(false);

  const handleValueChange = (newValue: string) => {
    onChange(parseInt(newValue) || 0);
  };

  return (
    <Select
      {...DATA_ALLOW_ARCHIVAL_EDIT}
      value={value?.toString() || ""}
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={setOpen}
    >
      <SelectTrigger className="h-9 text-xs border-white/10 bg-black/40 text-white hover:bg-black/50">
        {/* Custom render so the trigger label never depends on SelectItem children */}
        <SelectValue placeholder="Select note line...">
          {value && currentNoteLine
            ? `${currentNoteLine} (ID: ${value})`
            : value
            ? `(ID: ${value})`
            : "Select note line..."}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-black/95 border-white/10 max-h-80">
        {/* Only render options while open — avoids keeping 1667 × N fiber nodes alive */}
        {open && (options.length === 0 ? (
          <SelectItem value={value?.toString() || "0"}>
            {currentNoteLine || "No options available"}
          </SelectItem>
        ) : (
          options.map((option) => (
            <SelectItem
              key={option.note_line_id}
              value={option.note_line_id.toString()}
              className="text-white hover:bg-[#4da3ff]/20 focus:bg-[#4da3ff]/30"
            >
              {option.note_line} (ID: {option.note_line_id})
            </SelectItem>
          ))
        ))}
      </SelectContent>
    </Select>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.value === nextProps.value &&
    prevProps.currentNoteLine === nextProps.currentNoteLine &&
    prevProps.options.length === nextProps.options.length
  );
});

NoteLineDropdown.displayName = "NoteLineDropdown";

type ViewMode = "without-amounts" | "with-amounts";
type BookFilter = "all" | "SEZ" | "Non-SEZ";

type EditableDataWithoutAmounts = Record<
  string,
  {
    noteLine: string;
    noteLineId: number;
    fsSubLine: string;
    fsSubLineId: number;
    fsLine: string;
    fsLineId: number;
    type: string;
  }
>;

type EditableDataWithAmounts = Record<
  string,
  {
    noteLine: string;
    noteLineId: number;
    fsSubLine: string;
    fsSubLineId: number;
    fsLine: string;
    fsLineId: number;
    type: string;
  }
>;

export function TrialBalanceSection({ searchTerm }: TrialBalanceSectionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("without-amounts");
  const [bookFilter, setBookFilter] = useState<BookFilter>("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [debitCreditMetrics, setDebitCreditMetrics] = useState<any>(null);
  const [originalDataWithoutAmounts, setOriginalDataWithoutAmounts] = useState<MappingViewWithoutAmounts[]>([]);
  const [originalDataWithAmounts, setOriginalDataWithAmounts] = useState<MappingViewWithAmounts[]>([]);
  
  // Amounts view data (from JSON)
  const [amountsData, setAmountsData] = useState<Array<{
    bookName: string;
    ledgerId: number;
    ledgerName: string;
    balances: Record<string, number>; // date -> balance
  }>>([]);
  const [amountsDates, setAmountsDates] = useState<string[]>([]); // Sorted dates (latest first)
  const [loadAmountsLoading, setLoadAmountsLoading] = useState(false);

  const [sortBy, setSortBy] = useState<string>("ledgerName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [editableDataWithoutAmounts, setEditableDataWithoutAmounts] =
    useState<EditableDataWithoutAmounts>({});
  const [editableDataWithAmounts, setEditableDataWithAmounts] =
    useState<EditableDataWithAmounts>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Mapping section state
  const [mappingFilePath, setMappingFilePath] = useState("");
  const [mappingFileName, setMappingFileName] = useState("");
  const mappingBookNameRef = useRef<HTMLInputElement>(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingStatus, setMappingStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [loadTableLoading, setLoadTableLoading] = useState(false);
  const [mappingCompleteStatus, setMappingCompleteStatus] = useState<"complete" | "incomplete" | null>(null);

  // Advanced filter state
  interface MappingFilter {
    id: string;
    column: string;
    operator: string;
    value: string;
  }
  const [mappingFilters, setMappingFilters] = useState<MappingFilter[]>([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);

  // Filter column options for Mapping view
  const mappingFilterColumns = [
    { value: "bookName", label: "Book Name" },
    { value: "ledgerName", label: "Ledger Name" },
    { value: "ledgerId", label: "Ledger ID" },
    { value: "noteLine", label: "Note Line" },
    { value: "noteLineId", label: "Note Line ID" },
    { value: "fsSubLine", label: "FS Sub Line" },
    { value: "fsSubLineId", label: "FS Sub Line ID" },
    { value: "fsLine", label: "FS Line" },
    { value: "fsLineId", label: "FS Line ID" },
    { value: "type", label: "Type" },
  ];

  // Get filter column options for Amounts view (includes dynamic date columns)
  const getAmountsFilterColumns = () => {
    const baseColumns = [
      { value: "bookName", label: "Book Name" },
      { value: "ledgerName", label: "Ledger Name" },
      { value: "ledgerId", label: "Ledger ID" },
    ];
    // Add date columns dynamically
    const dateColumns = amountsDates.map((date) => ({
      value: date,
      label: `Balance (${date})`,
    }));
    return [...baseColumns, ...dateColumns];
  };

  // Filter operators
  const filterOperators = [
    { value: "contains", label: "Contains" },
    { value: "equals", label: "Equals" },
    { value: "startsWith", label: "Starts with" },
    { value: "endsWith", label: "Ends with" },
    { value: "notContains", label: "Does not contain" },
    { value: "isEmpty", label: "Is empty" },
    { value: "isNotEmpty", label: "Is not empty" },
  ];

  // Apply filter operator
  const applyFilterOperator = (cellValue: string | number, operator: string, filterValue: string): boolean => {
    const cellStr = String(cellValue || "").toLowerCase();
    const filterStr = filterValue.toLowerCase().trim();

    switch (operator) {
      case "contains":
        return cellStr.includes(filterStr);
      case "equals":
        return cellStr === filterStr;
      case "startsWith":
        return cellStr.startsWith(filterStr);
      case "endsWith":
        return cellStr.endsWith(filterStr);
      case "notContains":
        return !cellStr.includes(filterStr);
      case "isEmpty":
        return !cellValue || String(cellValue).trim() === "";
      case "isNotEmpty":
        return cellValue !== null && cellValue !== undefined && String(cellValue).trim() !== "";
      default:
        return true;
    }
  };

  // Apply filters to mapping data
  const applyMappingFilters = (data: MappingViewWithoutAmounts[], filters: MappingFilter[], incompleteOnly: boolean): MappingViewWithoutAmounts[] => {
    let filtered = [...data];

    // Apply "Show Incomplete" filter first
    if (incompleteOnly) {
      filtered = filtered.filter((item) => {
        const noteLine = item.noteLine || "";
        return !noteLine || noteLine.trim() === "";
      });
    }

    // Apply advanced filters
    if (filters.length > 0) {
      filtered = filtered.filter((row) => {
        return filters.every((filter) => {
          const filterValue = filter.value.trim();
          if (!filterValue && filter.operator !== "isEmpty" && filter.operator !== "isNotEmpty") {
            return true;
          }

          switch (filter.column) {
            case "bookName":
              return applyFilterOperator(row.bookName || "", filter.operator, filterValue);
            case "ledgerName":
              return applyFilterOperator(row.ledgerName || "", filter.operator, filterValue);
            case "ledgerId":
              return applyFilterOperator((row as any).ledgerId || (row as any).ledger_id || 0, filter.operator, filterValue);
            case "noteLine":
              return applyFilterOperator(row.noteLine || "", filter.operator, filterValue);
            case "noteLineId":
              return applyFilterOperator(row.noteLineId || 0, filter.operator, filterValue);
            case "fsSubLine":
              return applyFilterOperator(row.fsSubLine || "", filter.operator, filterValue);
            case "fsSubLineId":
              return applyFilterOperator((row as MappingViewWithoutAmounts).fsSubLineId || 0, filter.operator, filterValue);
            case "fsLine":
              return applyFilterOperator(row.fsLine || "", filter.operator, filterValue);
            case "fsLineId":
              return applyFilterOperator(row.fsLineId || 0, filter.operator, filterValue);
            case "type":
              return applyFilterOperator(row.type || "", filter.operator, filterValue);
            default:
              return true;
          }
        });
      });
    }

    return filtered;
  };

  // Add new filter
  const addMappingFilter = () => {
    const defaultColumn = viewMode === "without-amounts" ? "ledgerName" : "ledgerName";
    const newFilter: MappingFilter = {
      id: `filter-${Date.now()}`,
      column: defaultColumn,
      operator: "contains",
      value: "",
    };
    setMappingFilters([...mappingFilters, newFilter]);
  };

  // Remove filter
  const removeMappingFilter = (filterId: string) => {
    setMappingFilters(mappingFilters.filter((f) => f.id !== filterId));
  };

  // Update filter
  const updateMappingFilter = (filterId: string, updates: Partial<MappingFilter>) => {
    setMappingFilters(
      mappingFilters.map((f) => (f.id === filterId ? { ...f, ...updates } : f))
    );
  };

  // Clear all filters
  const clearAllMappingFilters = () => {
    setMappingFilters([]);
    setShowIncompleteOnly(false);
  };

  // Balance section state
  const [balanceFilePath, setBalanceFilePath] = useState("");
  const [balanceFileName, setBalanceFileName] = useState("");
  const balanceBookNameRef = useRef<HTMLInputElement>(null);
  const balanceDateInputRef = useRef<HTMLInputElement>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceStatus, setBalanceStatus] = useState<{ success: boolean; message: string } | null>(null);
  
  // Dropdown states
  const [isMappingDropdownOpen, setIsMappingDropdownOpen] = useState(false);
  const [isAmountsDropdownOpen, setIsAmountsDropdownOpen] = useState(false);
  const [isNoteLineDropdownOpen, setIsNoteLineDropdownOpen] = useState(false);
  const [isCloseDropdownOpen, setIsCloseDropdownOpen] = useState(false);

  // Close Trial Balance section state
  const openingDateRef = useRef<HTMLInputElement>(null);
  const [openingDate, setOpeningDate] = useState("");
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeStatus, setCloseStatus] = useState<{ success: boolean; message: string } | null>(null);

  // Add Note Line section state
  const noteLineIdRef = useRef<HTMLInputElement>(null);
  const noteLineRef = useRef<HTMLInputElement>(null);
  const fsSubLineRef = useRef<HTMLSelectElement>(null);
  const [noteLineLoading, setNoteLineLoading] = useState(false);
  const [noteLineStatus, setNoteLineStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [fsSublineOptions, setFsSublineOptions] = useState<Array<{ fs_sub_line_id: number; fs_sub_line: string }>>([]);
  const [fsSublineOptionsLoading, setFsSublineOptionsLoading] = useState(false);

  // Note Line mapping data from FinData_MappingData.json
  // Structure: [{ note_line_id: number; note_line: string }, ...]
  const [noteLineMappingData, setNoteLineMappingData] = useState<Array<{
    note_line_id: number;
    note_line: string;
  }>>([]);
  const [noteLineMappingLoading, setNoteLineMappingLoading] = useState(false);

  // Path to the temp file written by the main process on download.
  // We only keep the path in renderer state – never the full JSON object.
  const [mappingTempFilePath, setMappingTempFilePath] = useState<string>("");
  const [saveChangesLoading, setSaveChangesLoading] = useState(false);

  // Data loading function
  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading Trial Balance data...');

      const [debitCreditData, dataWithoutAmounts, dataWithAmounts] = await Promise.all([
        getDebitCreditBreakdown("combined"),
        getMappingViewWithoutAmounts(),
        getMappingViewWithAmounts()
      ]);

      console.log('Trial Balance data loaded successfully:', {
        debitCreditData: Object.keys(debitCreditData).length,
        dataWithoutAmounts: dataWithoutAmounts.length,
        dataWithAmounts: dataWithAmounts.length
      });

      setDebitCreditMetrics(debitCreditData);
      setOriginalDataWithoutAmounts(dataWithoutAmounts);
      setOriginalDataWithAmounts(dataWithAmounts);
      setDataLoaded(true);
    } catch (err) {
      console.error('Failed to load Trial Balance data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get current editable data based on view mode
  const currentEditableData =
    viewMode === "without-amounts"
      ? editableDataWithoutAmounts
      : editableDataWithAmounts;

  // Merge original data with editable changes
  const enhancedDataWithoutAmounts = originalDataWithoutAmounts.map((item) => ({
    ...item,
    noteLine:
      editableDataWithoutAmounts[item.ledgerName]?.noteLine ?? item.noteLine,
    noteLineId:
      editableDataWithoutAmounts[item.ledgerName]?.noteLineId ??
      item.noteLineId,
    fsSubLine:
      editableDataWithoutAmounts[item.ledgerName]?.fsSubLine ?? item.fsSubLine,
    fsSubLineId:
      editableDataWithoutAmounts[item.ledgerName]?.fsSubLineId ??
      item.fsSubLineId,
    fsLine: editableDataWithoutAmounts[item.ledgerName]?.fsLine ?? item.fsLine,
    fsLineId:
      editableDataWithoutAmounts[item.ledgerName]?.fsLineId ?? item.fsLineId,
    type: editableDataWithoutAmounts[item.ledgerName]?.type ?? item.type,
    ledgerId: (item as any).ledgerId ?? (item as any).ledger_id,
  }));

  const enhancedDataWithAmounts = originalDataWithAmounts.map((item) => ({
    ...item,
    noteLine:
      editableDataWithAmounts[item.ledgerName]?.noteLine ?? item.noteLine,
    noteLineId:
      editableDataWithAmounts[item.ledgerName]?.noteLineId ?? item.noteLineId,
    fsSubLine:
      editableDataWithAmounts[item.ledgerName]?.fsSubLine ?? item.fsSubLine,
    fsSubLineId:
      editableDataWithAmounts[item.ledgerName]?.fsSubLineId ?? item.fsSubLineId,
    fsLine: editableDataWithAmounts[item.ledgerName]?.fsLine ?? item.fsLine,
    fsLineId:
      editableDataWithAmounts[item.ledgerName]?.fsLineId ?? item.fsLineId,
    type: editableDataWithAmounts[item.ledgerName]?.type ?? item.type,
    ledgerId: (item as any).ledgerId ?? (item as any).ledger_id,
  }));

  // Get current data based on view mode
  const currentData =
    viewMode === "without-amounts"
      ? enhancedDataWithoutAmounts
      : enhancedDataWithAmounts;

  // Filter data based on search term and book filter
  const filteredData = currentData.filter((item) => {
    const matchesSearch =
      item.ledgerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.noteLine.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.fsLine.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.fsSubLine.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.bookName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesBookFilter =
      bookFilter === "all" || item.bookName === bookFilter;

    return matchesSearch && matchesBookFilter;
  });

  // Apply filters to filtered data
  const filteredDataWithFilters = useMemo(() => {
    if (viewMode === "without-amounts") {
      return applyMappingFilters(filteredData, mappingFilters, showIncompleteOnly);
    }
    return filteredData;
  }, [filteredData, mappingFilters, showIncompleteOnly, viewMode]);

  // Sort data
  const sortedData = [...filteredDataWithFilters].sort((a, b) => {
    const aValue = a[sortBy as keyof typeof a];
    const bValue = b[sortBy as keyof typeof b];

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortOrder === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Amounts view sorting
  const [amountsSortBy, setAmountsSortBy] = useState<string>("ledgerId");
  const [amountsSortOrder, setAmountsSortOrder] = useState<"asc" | "desc">("asc");

  const handleAmountsSort = (column: string) => {
    if (amountsSortBy === column) {
      setAmountsSortOrder(amountsSortOrder === "asc" ? "desc" : "asc");
    } else {
      setAmountsSortBy(column);
      setAmountsSortOrder("asc");
    }
  };

  // Filter amounts data based on search term and book filter
  const filteredAmountsData = amountsData.filter((item) => {
    const matchesSearch =
      item.ledgerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.bookName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.ledgerId.toString().includes(searchTerm);

    const matchesBookFilter =
      bookFilter === "all" || item.bookName === bookFilter;

    return matchesSearch && matchesBookFilter;
  });

  // Apply filters to amounts data
  const applyAmountsFilters = useCallback((data: Array<{
    bookName: string;
    ledgerId: number;
    ledgerName: string;
    balances: Record<string, number>;
  }>, filters: MappingFilter[], dates: string[]): Array<{
    bookName: string;
    ledgerId: number;
    ledgerName: string;
    balances: Record<string, number>;
  }> => {
    let filtered = [...data];

    // Apply advanced filters
    if (filters.length > 0) {
      filtered = filtered.filter((row) => {
        return filters.every((filter) => {
          const filterValue = filter.value.trim();
          if (!filterValue && filter.operator !== "isEmpty" && filter.operator !== "isNotEmpty") {
            return true;
          }

          switch (filter.column) {
            case "bookName":
              return applyFilterOperator(row.bookName || "", filter.operator, filterValue);
            case "ledgerName":
              return applyFilterOperator(row.ledgerName || "", filter.operator, filterValue);
            case "ledgerId":
              return applyFilterOperator(row.ledgerId || 0, filter.operator, filterValue);
            default:
              // Check if it's a date column
              if (dates.includes(filter.column)) {
                const balance = row.balances[filter.column] || 0;
                return applyFilterOperator(balance, filter.operator, filterValue);
              }
              return true;
          }
        });
      });
    }

    return filtered;
  }, []);

  // Apply filters to amounts data
  const filteredAmountsDataWithFilters = useMemo(() => {
    if (viewMode === "with-amounts") {
      return applyAmountsFilters(filteredAmountsData, mappingFilters, amountsDates);
    }
    return filteredAmountsData;
  }, [filteredAmountsData, mappingFilters, viewMode, amountsDates, applyAmountsFilters]);

  // Sort amounts data
  const sortedAmountsData = [...filteredAmountsDataWithFilters].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    if (amountsSortBy === "ledgerId") {
      aValue = a.ledgerId;
      bValue = b.ledgerId;
    } else if (amountsSortBy === "ledgerName") {
      aValue = a.ledgerName.toLowerCase();
      bValue = b.ledgerName.toLowerCase();
    } else if (amountsSortBy === "bookName") {
      aValue = a.bookName.toLowerCase();
      bValue = b.bookName.toLowerCase();
    } else {
      // For date columns, sort by the balance value for that date
      aValue = a.balances[amountsSortBy] || 0;
      bValue = b.balances[amountsSortBy] || 0;
    }

    if (aValue < bValue) {
      return amountsSortOrder === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return amountsSortOrder === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Load Note Line Mapping Data from FinData_MappingData.json
  const loadNoteLineMappingData = useCallback(async () => {
    setNoteLineMappingLoading(true);
    try {
      // Use downloadNoteLineMappings to get (note_line_id, note_line) pairs
      const result = await (window as any)?.sharePointAPI?.downloadNoteLineMappings?.();
      
      if (result?.success && result?.data) {
        // The data structure is: [{ note_line_id, note_line }, ...]
        setNoteLineMappingData(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error("Failed to load note line mapping data:", result?.error);
        setNoteLineMappingData([]);
      }
    } catch (error) {
      console.error("Failed to load note line mapping data:", error);
      setNoteLineMappingData([]);
    } finally {
      setNoteLineMappingLoading(false);
    }
  }, []);

  // Memoized note line options to prevent re-rendering
  // This is computed once and reused for all dropdowns to avoid lag
  const noteLineOptions = useMemo(() => {
    if (noteLineMappingData.length === 0) return [];
    
    // Data structure is already [{ note_line_id, note_line }, ...]
    // Just sort by note_line_id and return
    return [...noteLineMappingData].sort((a, b) => a.note_line_id - b.note_line_id);
  }, [noteLineMappingData]);

  const handleCellEdit = useCallback(
    (ledgerName: string, selectedNoteLineId: number, bookName?: string) => {
      // Find the selected note line from loaded data
      const selectedNoteLine = noteLineMappingData.find(
        (item) => item.note_line_id === selectedNoteLineId,
      );

      const newNoteLine = selectedNoteLine?.note_line || `Note Line ${selectedNoteLineId}`;

      // Find the ledger ID and book name from the current data
      const currentData = viewMode === "without-amounts" 
        ? originalDataWithoutAmounts 
        : originalDataWithAmounts;
      const currentItem = currentData.find(item => item.ledgerName === ledgerName);
      const targetLedgerId = currentItem?.ledgerId || (currentItem as any)?.ledger_id;
      const targetBookName = bookName || currentItem?.bookName || "";

      // Update the editable data for UI display
      if (!selectedNoteLine) {
        // If note line not found, still update with just the ID
        if (viewMode === "without-amounts") {
          setEditableDataWithoutAmounts((prev) => ({
            ...prev,
            [ledgerName]: {
              ...prev[ledgerName],
              noteLine: newNoteLine,
              noteLineId: selectedNoteLineId,
            },
          }));
        } else {
          setEditableDataWithAmounts((prev) => ({
            ...prev,
            [ledgerName]: {
              ...prev[ledgerName],
              noteLine: newNoteLine,
              noteLineId: selectedNoteLineId,
            },
          }));
        }
        setHasChanges(true);
        return;
      }

      // Only update Note Line and Note Line ID, keep other columns unchanged
      if (viewMode === "without-amounts") {
        setEditableDataWithoutAmounts((prev) => ({
          ...prev,
          [ledgerName]: {
            ...prev[ledgerName],
            noteLine: selectedNoteLine.note_line || "",
            noteLineId: selectedNoteLine.note_line_id,
          },
        }));
      } else {
        setEditableDataWithAmounts((prev) => ({
          ...prev,
          [ledgerName]: {
            ...prev[ledgerName],
            noteLine: selectedNoteLine.note_line || "",
            noteLineId: selectedNoteLine.note_line_id,
          },
        }));
      }
      setHasChanges(true);
    },
    [viewMode, noteLineMappingData, originalDataWithoutAmounts, originalDataWithAmounts],
  );

  // Handle Load Table from SharePoint - moved before handleSaveChanges to avoid initialization error
  const handleLoadTable = useCallback(async () => {
    setLoadTableLoading(true);
    setError(null);

    try {
      const api = (window as any)?.sharePointAPI;
      if (!api?.downloadTrialBalanceMapping) {
        setError(
          "Trial balance API is not available. Use the offline archive web app (choose the engagement ZIP first) or the desktop app so SharePoint / archive bindings are loaded.",
        );
        return;
      }

      await loadNoteLineMappingData();

      const result = await api.downloadTrialBalanceMapping({ includeAmounts: false });

      if (!result?.success) {
        setError(
          result?.error ||
            "Failed to load mapping data. In archive mode, ensure Juggernaut_Frontend.json or FinData_TrialBalance_Mapping.json is in the engagement ZIP.",
        );
        return;
      }

      if (!Array.isArray(result.mappingRows)) {
        setError("Invalid mapping response: mappingRows is missing. Check the trial balance mapping file in the archive.");
        return;
      }

      if (result.mappingRows.length === 0) {
        setError(
          "Trial balance mapping loaded but contains no ledger rows. The JSON may use an unsupported shape—ensure book keys (e.g. SEZ, Non SEZ) map to arrays of ledger objects with ledger_name / ledgerName.",
        );
        return;
      }

      setMappingTempFilePath(result.filePath ?? "");

      const newDataWithoutAmounts: MappingViewWithoutAmounts[] = result.mappingRows as MappingViewWithoutAmounts[];

      const isMappingComplete = newDataWithoutAmounts.every(
        (row) => row.noteLine && row.noteLine.trim() !== "",
      );
      setMappingCompleteStatus(isMappingComplete ? "complete" : "incomplete");

      // Apply mapping rows immediately so a failure in static Combined.json helpers does not wipe the load.
      setOriginalDataWithoutAmounts(newDataWithoutAmounts);
      setDataLoaded(true);

      try {
        const [debitCreditData, dataWithAmounts] = await Promise.all([
          getDebitCreditBreakdown("combined"),
          getMappingViewWithAmounts(),
        ]);
        setDebitCreditMetrics(debitCreditData);
        setOriginalDataWithAmounts(dataWithAmounts);
      } catch (suppErr) {
        console.warn("[TrialBalance] Supplementary static data (debit/credit breakdown) failed; mapping table still loaded.", suppErr);
      }
    } catch (error: any) {
      console.error('Failed to load Trial Balance data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load data');
    } finally {
      setLoadTableLoading(false);
    }
  }, [loadNoteLineMappingData]);

  const handleSaveChanges = useCallback(async () => {
    if (!mappingTempFilePath) {
      setError("No mapping data loaded. Please load mapping data first.");
      return;
    }

    setSaveChangesLoading(true);
    setError(null);

    try {
      // Build the list of changed entries from the editable state.
      // We merge both view-mode edit stores so all changes are captured.
      const mergedEdits: Record<string, {
        noteLine: string; noteLineId: number;
        fsSubLine: string; fsSubLineId: number;
        fsLine: string; fsLineId: number;
        type: string; bookName: string; ledgerId: number;
      }> = {};

      // Populate from originalDataWithoutAmounts so we always have bookName + ledgerId
      for (const row of originalDataWithoutAmounts) {
        const key = row.ledgerName;
        const edits = editableDataWithoutAmounts[key] ?? editableDataWithAmounts[key];
        if (edits) {
          mergedEdits[key] = {
            noteLine:    edits.noteLine    ?? row.noteLine,
            noteLineId:  edits.noteLineId  ?? row.noteLineId,
            fsSubLine:   edits.fsSubLine   ?? row.fsSubLine,
            fsSubLineId: edits.fsSubLineId ?? row.fsSubLineId,
            fsLine:      edits.fsLine      ?? row.fsLine,
            fsLineId:    edits.fsLineId    ?? row.fsLineId,
            type:        edits.type        ?? row.type,
            bookName:    row.bookName,
            ledgerId:    (row as any).ledgerId ?? (row as any).ledger_id ?? 0,
          };
        }
      }

      const changedEntries = Object.values(mergedEdits);

      // Send only the file path + lightweight changes (no full JSON over IPC)
      const result = await (window as any)?.sharePointAPI?.saveTrialBalanceMappingChanges?.({
        filePath: mappingTempFilePath,
        changedEntries,
      });

      if (result?.success) {
        setHasChanges(false);
        // Reload the data to reflect changes (also recalculates mapping status)
        await handleLoadTable();
        setError(null);
      } else {
        throw new Error(result?.error || "Failed to save changes");
      }
    } catch (error: any) {
      console.error('Failed to save changes:', error);
      setError(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setSaveChangesLoading(false);
    }
  }, [mappingTempFilePath, originalDataWithoutAmounts, editableDataWithoutAmounts, editableDataWithAmounts, handleLoadTable]);

  const handleResetChanges = () => {
    if (viewMode === "without-amounts") {
      setEditableDataWithoutAmounts({});
    } else {
      setEditableDataWithAmounts({});
    }
    setHasChanges(false);
  };

  const handleViewToggle = () => {
    setViewMode((prev) =>
      prev === "without-amounts" ? "with-amounts" : "without-amounts",
    );
    // Reset sort when switching views
    setSortBy("ledgerName");
    setSortOrder("asc");
  };

  const handleExportToExcel = () => {
    const exportData = sortedData.map((item) => {
      if (viewMode === "without-amounts") {
        return {
          "Book Name": item.bookName,
          "Ledger Name": item.ledgerName,
          "Ledger ID": (item as any).ledgerId || (item as any).ledger_id || "",
          "Note Line": item.noteLine,
          "Note Line ID": item.noteLineId,
          "FS Sub Line": item.fsSubLine,
          "FS Sub Line ID": (item as MappingViewWithoutAmounts).fsSubLineId,
          "FS Line": item.fsLine,
          "FS Line ID": item.fsLineId,
          Type: item.type,
        };
      } else {
        const itemWithAmounts = item as MappingViewWithAmounts;
        return {
          "Ledger Name": item.ledgerName,
          "Book Name": item.bookName,
          "Opening Balance": itemWithAmounts.openingBalance.toLocaleString(
            "en-IN",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          ),
          "Closing Balance": itemWithAmounts.closingBalance.toLocaleString(
            "en-IN",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          ),
        };
      }
    });

    const headers = Object.keys(exportData[0] ?? {});
    const csvContent = [
      headers.join(","),
      ...exportData.map((row) =>
        headers
          .map((header) => `"${row[header as keyof typeof row]}"`)
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const bookFilterSuffix =
      bookFilter === "all" ? "combined" : bookFilter.toLowerCase();
    link.setAttribute(
      "download",
      `mapping_view_${viewMode}_${bookFilterSuffix}_${
        new Date().toISOString().split("T")[0]
      }.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getViewDescription = () => {
    return viewMode === "without-amounts"
      ? "Mapping view focused on account structure with dropdown selection for Note Line fields from predefined options"
      : "Simple view with Ledger Name, Opening Balance, and Closing Balance in exact formatted numbers";
  };

  // Handle Mapping file selection via dialog
  const handleMappingFileSelect = useCallback(async () => {
    try {
      const result = await (window as any)?.sharePointAPI?.openFileDialog?.({
        filters: [{ name: "Excel Files", extensions: ["xlsx", "xls"] }]
      });
      
      if (result?.success && result?.filePath) {
        setMappingFilePath(result.filePath);
        // Extract filename from path
        const fileName = result.filePath.split(/[/\\]/).pop() || result.filePath;
        setMappingFileName(fileName);
        setMappingStatus(null);
      }
    } catch (error: any) {
      setMappingStatus({
        success: false,
        message: error?.message || "Failed to select file"
      });
    }
  }, []);

  // Handle Load Amounts from SharePoint
  // Re-downloads the mapping (which now returns pre-processed amountsData from main process).
  const handleLoadAmounts = useCallback(async () => {
    setLoadAmountsLoading(true);
    setError(null);

    try {
      // The IPC handler processes everything in the main process and returns
      // amountsData + availableDates directly – no large JSON crosses IPC.
      // Request amounts explicitly (this is a separate call, so it's OK to be larger)
      const result = await (window as any)?.sharePointAPI?.downloadTrialBalanceMapping?.({ includeAmounts: true });

      if (!result?.success) {
        setError(
          result?.error ||
            "Failed to load amounts data. In archive mode, ledger rows need an \"amount\" object with date keys (dd/mm/yy) in the mapping JSON.",
        );
        return;
      }

      if (!Array.isArray(result.amountsData)) {
        setError("Invalid mapping response: amountsData is missing.");
        return;
      }

      setMappingTempFilePath(result.filePath ?? mappingTempFilePath);
      setAmountsData(result.amountsData);
      setAmountsDates(result.availableDates ?? []);
      setDataLoaded(true);
    } catch (error: any) {
      console.error('Failed to load Trial Balance amounts:', error);
      setError(error instanceof Error ? error.message : 'Failed to load amounts data');
    } finally {
      setLoadAmountsLoading(false);
    }
  }, [mappingTempFilePath]);

  // Handle Create Mappings
  const handleCreateMappings = useCallback(async () => {
    const bookName = mappingBookNameRef.current?.value?.trim() || "";
    
    if (!mappingFilePath.trim() || !bookName) {
      setMappingStatus({
        success: false,
        message: "Please select a file and enter a book name"
      });
      return;
    }

    setMappingLoading(true);
    setMappingStatus(null);

    try {
      const result = await (window as any)?.sharePointAPI?.createTrialBalanceMappings?.({
        filePath: mappingFilePath.trim(),
        bookName: bookName
      });

      if (result?.success) {
        setMappingStatus({
          success: true,
          message: result.message || "Mappings created successfully"
        });
        setMappingFilePath("");
        setMappingFileName("");
        if (mappingBookNameRef.current) {
          mappingBookNameRef.current.value = "";
        }
      } else {
        setMappingStatus({
          success: false,
          message: result?.error || "Failed to create mappings"
        });
      }
    } catch (error: any) {
      setMappingStatus({
        success: false,
        message: error?.message || "An error occurred while creating mappings"
      });
    } finally {
      setMappingLoading(false);
    }
  }, [mappingFilePath]);

  // Handle Balance file selection via dialog
  const handleBalanceFileSelect = useCallback(async () => {
    try {
      const result = await (window as any)?.sharePointAPI?.openFileDialog?.({
        filters: [{ name: "Excel Files", extensions: ["xlsx", "xls"] }]
      });
      
      if (result?.success && result?.filePath) {
        setBalanceFilePath(result.filePath);
        // Extract filename from path
        const fileName = result.filePath.split(/[/\\]/).pop() || result.filePath;
        setBalanceFileName(fileName);
        setBalanceStatus(null);
      }
    } catch (error: any) {
      setBalanceStatus({
        success: false,
        message: error?.message || "Failed to select file"
      });
    }
  }, []);

  // Handle Extract Amounts
  const handleExtractAmounts = useCallback(async () => {
    const bookName = balanceBookNameRef.current?.value?.trim() || "";
    const dateInput = balanceDateInputRef.current?.value?.trim() || "";
    
    if (!balanceFilePath.trim() || !bookName || !dateInput) {
      setBalanceStatus({
        success: false,
        message: "Please select a file, enter a book name, and date"
      });
      return;
    }

    // Validate date format (dd/mm/yy)
    const dateRegex = /^\d{2}\/\d{2}\/\d{2}$/;
    if (!dateRegex.test(dateInput)) {
      setBalanceStatus({
        success: false,
        message: "Date must be in dd/mm/yy format"
      });
      return;
    }

    setBalanceLoading(true);
    setBalanceStatus(null);

    try {
      const result = await (window as any)?.sharePointAPI?.extractTrialBalanceAmounts?.({
        filePath: balanceFilePath.trim(),
        bookName: bookName,
        dateInput: dateInput
      });

      if (result?.success) {
        setBalanceStatus({
          success: true,
          message: result.message || "Amounts extracted successfully"
        });
        setBalanceFilePath("");
        setBalanceFileName("");
        if (balanceBookNameRef.current) {
          balanceBookNameRef.current.value = "";
        }
        if (balanceDateInputRef.current) {
          balanceDateInputRef.current.value = "";
        }
      } else {
        setBalanceStatus({
          success: false,
          message: result?.error || "Failed to extract amounts"
        });
      }
    } catch (error: any) {
      setBalanceStatus({
        success: false,
        message: error?.message || "An error occurred while extracting amounts"
      });
    } finally {
      setBalanceLoading(false);
    }
  }, [balanceFilePath]);

  // Handle Close Trial Balance
  const handleCloseTrialBalance = useCallback(async () => {
    const dateValue = openingDate.trim();
    
    if (!dateValue) {
      setCloseStatus({
        success: false,
        message: "Please enter an opening date"
      });
      return;
    }

    // Validate date format (dd/mm/yy)
    const dateRegex = /^\d{2}\/\d{2}\/\d{2}$/;
    if (!dateRegex.test(dateValue)) {
      setCloseStatus({
        success: false,
        message: "Date must be in dd/mm/yy format (e.g., 31/03/24)"
      });
      return;
    }

    setCloseLoading(true);
    setCloseStatus(null);

    try {
      const result = await (window as any)?.sharePointAPI?.closeTrialBalance?.({
        openingDate: dateValue
      });

      if (result?.success) {
        setCloseStatus({
          success: true,
          message: result.message || "Trial Balance closed successfully"
        });
        setOpeningDate("");
        if (openingDateRef.current) {
          openingDateRef.current.value = "";
        }
      } else {
        setCloseStatus({
          success: false,
          message: result?.error || "Failed to close Trial Balance"
        });
      }
    } catch (error: any) {
      setCloseStatus({
        success: false,
        message: error?.message || "An error occurred while closing Trial Balance"
      });
    } finally {
      setCloseLoading(false);
    }
  }, [openingDate]);

  // Load FS Subline Options
  const loadFsSublineOptions = useCallback(async () => {
    if (fsSublineOptions.length > 0) return; // Already loaded
    
    setFsSublineOptionsLoading(true);
    try {
      const result = await (window as any)?.sharePointAPI?.downloadFsSublineOptions?.();
      
      if (result?.success && result?.data) {
        setFsSublineOptions(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error("Failed to load FS subline options:", result?.error);
        setFsSublineOptions([]);
      }
    } catch (error) {
      console.error("Failed to load FS subline options:", error);
      setFsSublineOptions([]);
    } finally {
      setFsSublineOptionsLoading(false);
    }
  }, [fsSublineOptions.length]);

  // Handle Add Note Line
  const handleAddNoteLine = useCallback(async () => {
    const noteLineId = noteLineIdRef.current?.value?.trim() || "";
    const noteLine = noteLineRef.current?.value?.trim() || "";
    const fsSubLineId = fsSubLineRef.current?.value?.trim() || "";
    
    if (!noteLineId || !noteLine || !fsSubLineId) {
      setNoteLineStatus({
        success: false,
        message: "Please fill in all fields"
      });
      return;
    }

    // Validate numeric field
    const noteLineIdNum = parseInt(noteLineId);
    if (isNaN(noteLineIdNum)) {
      setNoteLineStatus({
        success: false,
        message: "Note Line ID must be a valid number"
      });
      return;
    }

    // Validate Note Line ID is NOT in range (30000 - 30185)
    if (noteLineIdNum >= 30000 && noteLineIdNum <= 30185) {
      setNoteLineStatus({
        success: false,
        message: "Note Line ID must NOT be between 30000 and 30185"
      });
      return;
    }

    // Find the selected FS Subline from options
    const selectedFsSubline = fsSublineOptions.find(
      (opt) => opt.fs_sub_line_id.toString() === fsSubLineId
    );

    if (!selectedFsSubline) {
      setNoteLineStatus({
        success: false,
        message: "Please select a valid FS Subline"
      });
      return;
    }

    const fsSubLine = selectedFsSubline.fs_sub_line;

    setNoteLineLoading(true);
    setNoteLineStatus(null);

    try {
      const result = await (window as any)?.sharePointAPI?.addNoteLine?.({
        noteLineId: noteLineIdNum,
        noteLine: noteLine,
        fsSubLine: fsSubLine
      });

      if (!result) {
        setNoteLineStatus({
          success: false,
          message: "No response received from server. The operation may have timed out or the service is unavailable."
        });
        return;
      }

      if (result.success) {
        setNoteLineStatus({
          success: true,
          message: result.message || "Note line added successfully"
        });
        // Clear all input fields
        if (noteLineIdRef.current) noteLineIdRef.current.value = "";
        if (noteLineRef.current) noteLineRef.current.value = "";
        if (fsSubLineRef.current) fsSubLineRef.current.value = "";
      } else {
        const errorMessage = 
          result.error || 
          result.message || 
          (typeof result === 'string' ? result : null) ||
          "Failed to add note line. Please check all fields are filled correctly and try again.";
        
        setNoteLineStatus({
          success: false,
          message: errorMessage
        });
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.error || String(error) || "An error occurred while adding note line";
      setNoteLineStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setNoteLineLoading(false);
    }
  }, [fsSublineOptions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading Trial Balance data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-lg px-4">
          <div className="text-red-400 mb-2 font-medium">Error loading data</div>
          <div className="text-sm text-white/80">{error}</div>
          <Button
            {...DATA_ALLOW_ARCHIVAL_EDIT}
            onClick={() => {
              setError(null);
              void loadFinancialData();
            }}
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-white">
      {/* Upload Mapping Dropdown */}
      <Card className="overflow-hidden">
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setIsMappingDropdownOpen(!isMappingDropdownOpen)}
        >
          <CardHeader className="hover:bg-white/5 transition-colors">
            <CardTitle className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Mapping
              </div>
              {isMappingDropdownOpen ? (
                <ChevronUp className="h-5 w-5 text-white/60 transition-transform" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/60 transition-transform" />
              )}
            </CardTitle>
            <CardDescription>
              Upload an Excel file and create mappings for Trial Balance data
            </CardDescription>
          </CardHeader>
        </button>
        {isMappingDropdownOpen && (
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80">Select Excel File</label>
              <div className="flex items-center gap-2">
                <Button
                  {...DATA_ALLOW_ARCHIVAL_EDIT}
                  type="button"
                  variant="outline"
                  onClick={handleMappingFileSelect}
                  className="border-white/10 bg-white/5 text-white hover:bg-white/10"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Choose File
                </Button>
                {mappingFileName && (
                  <span className="text-sm text-white/80 flex-1 truncate">{mappingFileName}</span>
                )}
              </div>
              {mappingFilePath && (
                <p className="text-xs text-white/40 truncate">Path: {mappingFilePath}</p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80">Book Name</label>
              <Input
                ref={mappingBookNameRef}
                type="text"
                placeholder="e.g., SEZ, Non-SEZ"
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
              />
            </div>
            <Button
              onClick={handleCreateMappings}
              disabled={mappingLoading || !mappingFilePath.trim()}
              className="bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
            >
              {mappingLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Mappings...
                </>
              ) : (
                <>
                  <Calculator className="h-4 w-4 mr-2" />
                  Create Mappings
                </>
              )}
            </Button>
            {mappingStatus && (
              <div className={`p-3 rounded-md ${mappingStatus.success ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                {mappingStatus.message}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Upload Amounts Dropdown */}
      <Card className="overflow-hidden">
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setIsAmountsDropdownOpen(!isAmountsDropdownOpen)}
        >
          <CardHeader className="hover:bg-white/5 transition-colors">
            <CardTitle className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Upload Amounts
              </div>
              {isAmountsDropdownOpen ? (
                <ChevronUp className="h-5 w-5 text-white/60 transition-transform" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/60 transition-transform" />
              )}
            </CardTitle>
            <CardDescription>
              Upload an Excel file and extract balance amounts for Trial Balance data
            </CardDescription>
          </CardHeader>
        </button>
        {isAmountsDropdownOpen && (
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80">Select Excel File</label>
              <div className="flex items-center gap-2">
                <Button
                  {...DATA_ALLOW_ARCHIVAL_EDIT}
                  type="button"
                  variant="outline"
                  onClick={handleBalanceFileSelect}
                  className="border-white/10 bg-white/5 text-white hover:bg-white/10"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Choose File
                </Button>
                {balanceFileName && (
                  <span className="text-sm text-white/80 flex-1 truncate">{balanceFileName}</span>
                )}
              </div>
              {balanceFilePath && (
                <p className="text-xs text-white/40 truncate">Path: {balanceFilePath}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">Book Name</label>
                <Input
                  ref={balanceBookNameRef}
                  type="text"
                  placeholder="e.g., SEZ, Non-SEZ"
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">Date (dd/mm/yy)</label>
                <Input
                  ref={balanceDateInputRef}
                  type="text"
                  placeholder="31/03/25"
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                />
              </div>
            </div>
            <Button
              onClick={handleExtractAmounts}
              disabled={balanceLoading || !balanceFilePath.trim()}
              className="bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
            >
              {balanceLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Extracting Amounts...
                </>
              ) : (
                <>
                  <Calculator className="h-4 w-4 mr-2" />
                  Extract Amounts
                </>
              )}
            </Button>
            {balanceStatus && (
              <div className={`p-3 rounded-md ${balanceStatus.success ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                {balanceStatus.message}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Add Note Line Dropdown */}
      <Card className="overflow-hidden">
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setIsNoteLineDropdownOpen(!isNoteLineDropdownOpen)}
        >
          <CardHeader className="hover:bg-white/5 transition-colors">
            <CardTitle className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Add Note Line
              </div>
              {isNoteLineDropdownOpen ? (
                <ChevronUp className="h-5 w-5 text-white/60 transition-transform" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/60 transition-transform" />
              )}
            </CardTitle>
            <CardDescription>
              Add a new note line entry to the mapping data
            </CardDescription>
          </CardHeader>
        </button>
        {isNoteLineDropdownOpen && (
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">Note Line ID (Integer)</label>
                <Input
                  ref={noteLineIdRef}
                  type="number"
                  placeholder="e.g., 30186 (NOT 30000-30185)"
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                  onBlur={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 30000 && value <= 30185) {
                      setNoteLineStatus({
                        success: false,
                        message: "Note Line ID must NOT be between 30000 and 30185"
                      });
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">Note Line (String)</label>
                <Input
                  ref={noteLineRef}
                  type="text"
                  placeholder="e.g., Share Capital"
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">FS Subline (String)</label>
                <select
                  ref={fsSubLineRef}
                  onClick={loadFsSublineOptions}
                  className="w-full rounded border border-white/10 bg-black/40 p-2 text-sm text-white backdrop-blur-sm placeholder:text-white/40 focus:border-[#4da3ff]/60 focus:ring-1 focus:ring-[#4da3ff]/40 focus:outline-none"
                  disabled={fsSublineOptionsLoading}
                >
                  <option value="">{fsSublineOptionsLoading ? "Loading..." : "Select FS Subline"}</option>
                  {fsSublineOptions.map((option) => (
                    <option key={option.fs_sub_line_id} value={option.fs_sub_line_id}>
                      {option.fs_sub_line} (ID: {option.fs_sub_line_id})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Button
              onClick={handleAddNoteLine}
              disabled={noteLineLoading}
              className="bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
            >
              {noteLineLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Add
                </>
              )}
            </Button>
            {noteLineStatus && (
              <div className={`p-3 rounded-md ${noteLineStatus.success ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                <div className="break-words whitespace-pre-wrap">{noteLineStatus.message}</div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Close Trial Balance Dropdown */}
      <Card className="overflow-hidden border-2 border-blue-500/60 bg-blue-500/15 shadow-lg shadow-blue-500/20">
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setIsCloseDropdownOpen(!isCloseDropdownOpen)}
        >
          <CardHeader className="hover:bg-blue-500/15 transition-colors">
            <CardTitle className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-blue-400" />
                <span className="text-blue-300 font-semibold">Close Trial Balance</span>
              </div>
              {isCloseDropdownOpen ? (
                <ChevronUp className="h-5 w-5 text-blue-400/70 transition-transform" />
              ) : (
                <ChevronDown className="h-5 w-5 text-blue-400/70 transition-transform" />
              )}
            </CardTitle>
            <CardDescription className="text-blue-200/90">
              Only do this when all Trial balance entries are done. If any updates after closing, make sure to re-run this as well
            </CardDescription>
          </CardHeader>
        </button>
        {isCloseDropdownOpen && (
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80">Opening Date (dd/mm/yy)</label>
              <Input
                ref={openingDateRef}
                type="text"
                placeholder="31/03/24"
                value={openingDate}
                onChange={(e) => setOpeningDate(e.target.value)}
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
              />
              <p className="text-xs text-white/40">Enter the opening date in dd/mm/yy format</p>
            </div>
            <Button
              onClick={handleCloseTrialBalance}
              disabled={closeLoading || !openingDate.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md shadow-blue-600/30 disabled:bg-blue-600/50 disabled:hover:bg-blue-600/50 disabled:shadow-none"
            >
              {closeLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Closing Trial Balance...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Close Trial Balance
                </>
              )}
            </Button>
            {closeStatus && (
              <div className={`p-3 rounded-md ${closeStatus.success ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                {closeStatus.message}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Trial Balance Mapping Views
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-white/10 text-white/80">
            {viewMode === "with-amounts" 
              ? (mappingFilters.length > 0 ? sortedAmountsData.length : filteredAmountsData.length)
              : (mappingFilters.length > 0 || showIncompleteOnly 
                  ? sortedData.length 
                  : filteredData.length)} Entries
          </Badge>

          {/* View Toggle */}
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 backdrop-blur-sm">
            <Eye className="h-4 w-4 text-white/80" />
            <span className="text-sm font-medium text-white/80">
              {viewMode === "without-amounts" ? "Mapping" : "Amounts"}
            </span>
            <Button
              {...DATA_ALLOW_ARCHIVAL_EDIT}
              variant="ghost"
              size="sm"
              onClick={handleViewToggle}
              className="h-auto p-1"
            >
              {viewMode === "without-amounts" ? (
                <ToggleLeft className="h-5 w-5" />
              ) : (
                <ToggleRight className="h-5 w-5 text-emerald-400" />
              )}
            </Button>
          </div>

          {/* Load Mapping/Amounts Button - shows different button based on view mode */}
          {viewMode === "without-amounts" ? (
            <Button
              {...DATA_ALLOW_ARCHIVAL_EDIT}
              size="sm"
              onClick={handleLoadTable}
              disabled={loadTableLoading}
              aria-busy={loadTableLoading}
              className={`relative min-w-[10.5rem] justify-center bg-emerald-600 text-white hover:bg-emerald-700 ${
                loadTableLoading
                  ? "!opacity-100 cursor-wait shadow-[0_0_24px_rgba(16,185,129,0.55)] ring-2 ring-white/40 ring-offset-2 ring-offset-black/20"
                  : ""
              }`}
            >
              {loadTableLoading ? (
                <>
                  <span className="relative mr-2 inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <span
                      className="absolute h-4 w-4 rounded-full border-2 border-white/25 border-t-white animate-spin"
                      style={{ animationDuration: "0.65s" }}
                    />
                    <Loader2 className="relative h-3.5 w-3.5 text-white" aria-hidden />
                  </span>
                  <span className="relative">Loading mapping…</span>
                </>
              ) : (
                <>
                  <Download className="relative mr-1 h-4 w-4 shrink-0" />
                  Load Mapping
                </>
              )}
            </Button>
          ) : (
            <Button
              {...DATA_ALLOW_ARCHIVAL_EDIT}
              size="sm"
              onClick={handleLoadAmounts}
              disabled={loadAmountsLoading}
              aria-busy={loadAmountsLoading}
              className={`relative min-w-[10.5rem] justify-center bg-emerald-600 text-white hover:bg-emerald-700 ${
                loadAmountsLoading
                  ? "!opacity-100 cursor-wait shadow-[0_0_24px_rgba(16,185,129,0.55)] ring-2 ring-white/40 ring-offset-2 ring-offset-black/20"
                  : ""
              }`}
            >
              {loadAmountsLoading ? (
                <>
                  <span className="relative mr-2 inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <span
                      className="absolute h-4 w-4 rounded-full border-2 border-white/25 border-t-white animate-spin"
                      style={{ animationDuration: "0.65s" }}
                    />
                    <Loader2 className="relative h-3.5 w-3.5 text-white" aria-hidden />
                  </span>
                  <span className="relative">Loading amounts…</span>
                </>
              ) : (
                <>
                  <Download className="relative mr-1 h-4 w-4 shrink-0" />
                  Load Amounts
                </>
              )}
            </Button>
          )}

          {hasChanges && (
            <div className="flex gap-2">
              <Button
                {...DATA_ALLOW_ARCHIVAL_EDIT}
                size="sm"
                variant="outline"
                onClick={handleResetChanges}
                className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
              >
                <Undo2 className="mr-1 h-4 w-4" />
                Reset
              </Button>
              <Button
                size="sm"
                onClick={handleSaveChanges}
                disabled={saveChangesLoading}
                className="bg-[#4da3ff] text-black hover:bg-[#4da3ff]/90"
              >
                {saveChangesLoading ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-1 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}

          <Button
            {...DATA_ALLOW_ARCHIVAL_EDIT}
            size="sm"
            variant="outline"
            onClick={handleExportToExcel}
            className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <Download className="mr-1 h-4 w-4" />
            Export Excel
          </Button>

          {/* Filter button - show in both views */}
          <Button
            {...DATA_ALLOW_ARCHIVAL_EDIT}
            size="sm"
            variant="outline"
            onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
            className={`border-white/10 text-white/80 hover:bg-white/10 hover:text-white ${
              isFilterPanelOpen ? "bg-white/10" : ""
            }`}
          >
            <Filter className="mr-1 h-4 w-4" />
            Filter
            {mappingFilters.length > 0 && (
              <Badge variant="outline" className="ml-2 text-xs">
                {mappingFilters.length}
              </Badge>
            )}
          </Button>

          {/* Show Incomplete button - only show in Mapping view */}
          {viewMode === "without-amounts" && (
            <Button
              {...DATA_ALLOW_ARCHIVAL_EDIT}
              size="sm"
              variant="outline"
              onClick={() => setShowIncompleteOnly(!showIncompleteOnly)}
              className={`border-white/10 text-white/80 hover:bg-white/10 hover:text-white ${
                showIncompleteOnly ? "bg-red-500/20 border-red-500/50 text-red-400" : ""
              }`}
            >
              Show Incomplete
            </Button>
          )}
        </div>
      </div>

      {/* Advanced Filter Panel - show in both views */}
      {isFilterPanelOpen && (
        <Card className="border-white/10 bg-white/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-white/60" />
                <CardTitle className="text-base font-medium text-white">Advanced Filters</CardTitle>
                {mappingFilters.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {mappingFilters.length} active
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {mappingFilters.length > 0 && (
                  <Button
                    {...DATA_ALLOW_ARCHIVAL_EDIT}
                    size="sm"
                    variant="ghost"
                    onClick={clearAllMappingFilters}
                    className="text-xs text-white/60 hover:text-white hover:bg-white/10"
                  >
                    Clear All
                  </Button>
                )}
                <Button
                  {...DATA_ALLOW_ARCHIVAL_EDIT}
                  size="sm"
                  variant="outline"
                  onClick={addMappingFilter}
                  className="text-white border-white/20 hover:bg-white/10"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Filter
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {mappingFilters.length === 0 ? (
              <div className="text-center py-4 text-white/40 text-sm">
                No filters applied. Click "Add Filter" to start filtering.
              </div>
            ) : (
              <div className="space-y-3">
                {mappingFilters.map((filter) => (
                  <div
                    key={filter.id}
                    className="flex items-center gap-2 p-3 rounded-md border border-white/10 bg-black/20"
                  >
                    <Select
                      {...DATA_ALLOW_ARCHIVAL_EDIT}
                      value={filter.column}
                      onValueChange={(value) =>
                        updateMappingFilter(filter.id, { column: value })
                      }
                    >
                      <SelectTrigger className="w-[180px] h-9 text-sm">
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        {(viewMode === "without-amounts" ? mappingFilterColumns : getAmountsFilterColumns()).map((col) => (
                          <SelectItem key={col.value} value={col.value}>
                            {col.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      {...DATA_ALLOW_ARCHIVAL_EDIT}
                      value={filter.operator}
                      onValueChange={(value) =>
                        updateMappingFilter(filter.id, { operator: value })
                      }
                    >
                      <SelectTrigger className="w-[140px] h-9 text-sm">
                        <SelectValue placeholder="Operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {filterOperators.map((op) => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {(filter.operator !== "isEmpty" && filter.operator !== "isNotEmpty") && (
                      <Input
                        type="text"
                        placeholder="Filter value..."
                        value={filter.value}
                        onChange={(e) =>
                          updateMappingFilter(filter.id, { value: e.target.value })
                        }
                        className="flex-1 h-9 bg-black/20 border-white/10 text-white placeholder:text-white/40"
                      />
                    )}

                    <Button
                      {...DATA_ALLOW_ARCHIVAL_EDIT}
                      size="sm"
                      variant="ghost"
                      onClick={() => removeMappingFilter(filter.id)}
                      className="h-9 w-9 p-0 text-white/60 hover:text-white hover:bg-white/10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mapping View Table */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                {viewMode === "without-amounts" ? (
                  <Eye className="h-5 w-5" />
                ) : (
                  <DollarSign className="h-5 w-5" />
                )}
                {viewMode === "without-amounts" ? "Mapping View " : "Amounts View"}
              </CardTitle>
              <CardDescription>
                {viewMode === "without-amounts"
                  ? `${
                      bookFilter === "all" ? "Combined" : bookFilter
                    } data with dropdown selection for Note Line from predefined mapping options. Select from dropdown to update both Note Line and Note Line ID together.`
                  : `${
                      bookFilter === "all" ? "Combined" : bookFilter
                    } data showing Book Name, Ledger ID, Ledger Name, and balance amounts by date.`}
              </CardDescription>
            </div>
            {viewMode === "without-amounts" && mappingCompleteStatus && (
              <div className="ml-4">
                <Badge
                  variant="outline"
                  className={`text-sm font-medium ${
                    mappingCompleteStatus === "complete"
                      ? "border-green-500/50 bg-green-500/20 text-green-400"
                      : "border-red-500/50 bg-red-500/20 text-red-400"
                  }`}
                >
                  Status: Mapping {mappingCompleteStatus === "complete" ? "Complete" : "Incomplete"}
                </Badge>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {viewMode === "with-amounts" ? (
              // Amounts view table with Book Name, Ledger ID, Ledger Name, then date columns
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[100px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleAmountsSort("bookName")}
                        className="h-auto p-0 font-semibold"
                      >
                        Book Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[100px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleAmountsSort("ledgerId")}
                        className="h-auto p-0 font-semibold"
                      >
                        Ledger ID
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[250px]">
                      <Button
                        variant="ghost"
                        onClick={() => handleAmountsSort("ledgerName")}
                        className="h-auto p-0 font-semibold"
                      >
                        Ledger Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    {amountsDates.map((date) => (
                      <TableHead key={date} className="min-w-[120px] text-right">
                        <Button
                          {...DATA_ALLOW_ARCHIVAL_EDIT}
                          variant="ghost"
                          onClick={() => handleAmountsSort(date)}
                          className="h-auto p-0 font-semibold"
                        >
                          {date}
                          <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAmountsData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3 + amountsDates.length} className="text-center text-white/60 py-8">
                        No amounts data loaded. Click "Load Amounts" to load data from SharePoint.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedAmountsData.map((item, index) => (
                      <TableRow
                        key={`${item.bookName}-${item.ledgerId}-${index}`}
                        className="hover:bg-white/5"
                      >
                        <TableCell className="text-center">
                          <Badge
                            variant={item.bookName === "SEZ" ? "default" : "secondary"}
                            className="border-white/10 bg-white/5 text-xs text-white/80"
                          >
                            {item.bookName}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">
                          {item.ledgerId}
                        </TableCell>
                        <TableCell className="text-sm font-medium" title={item.ledgerName}>
                          <div className="max-w-[250px] truncate">{item.ledgerName}</div>
                        </TableCell>
                        {amountsDates.map((date) => (
                          <TableCell key={date} className="text-right font-mono text-sm">
                            {(item.balances[date] ?? 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : (
              // Mapping view table (existing)
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[100px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("bookName")}
                        className="h-auto p-0 font-semibold"
                      >
                        Book Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[250px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("ledgerName")}
                        className="h-auto p-0 font-semibold"
                      >
                        Ledger Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[100px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("ledgerId")}
                        className="h-auto p-0 font-semibold"
                      >
                        Ledger ID
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[200px] bg-white/5">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("noteLine")}
                        className="h-auto p-0 font-semibold"
                      >
                        Note Line (Select from dropdown)
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[150px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("fsSubLine")}
                        className="h-auto p-0 font-semibold"
                      >
                        FS Sub Line
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[120px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("fsSubLineId")}
                        className="h-auto p-0 font-semibold"
                      >
                        FS Sub Line ID
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[120px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("fsLine")}
                        className="h-auto p-0 font-semibold"
                      >
                        FS Line
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[100px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("fsLineId")}
                        className="h-auto p-0 font-semibold"
                      >
                        FS Line ID
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[100px]">
                      <Button
                        {...DATA_ALLOW_ARCHIVAL_EDIT}
                        variant="ghost"
                        onClick={() => handleSort("type")}
                        className="h-auto p-0 font-semibold"
                      >
                        Type
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-white/60 py-8">
                        {dataLoaded
                          ? "No rows match the current search or filters. Clear filters or adjust the search box."
                          : "No mapping data loaded. Click \"Load Mapping\" to load from SharePoint or the engagement archive."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((item, index) => (
                      <TableRow
                        key={`${item.ledgerName}-${index}`}
                        className="hover:bg-white/5"
                      >
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            item.bookName === "SEZ" ? "default" : "secondary"
                          }
                          className="border-white/10 bg-white/5 text-xs text-white/80"
                        >
                          {item.bookName}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="max-w-[250px] text-sm font-medium"
                        title={item.ledgerName}
                      >
                        <div className="truncate">{item.ledgerName}</div>
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {(item as any).ledgerId || (item as any).ledger_id || "-"}
                      </TableCell>
                      <TableCell className="bg-white/5 p-1">
                        <NoteLineDropdown
                          value={item.noteLineId}
                          onChange={(noteLineId) => handleCellEdit(item.ledgerName, noteLineId, item.bookName)}
                          options={noteLineOptions}
                          currentNoteLine={item.noteLine}
                        />
                      </TableCell>
                      <TableCell className="text-sm" title={item.fsSubLine}>
                        <div className="max-w-[150px] truncate">
                          {item.fsSubLine}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {(item as MappingViewWithoutAmounts).fsSubLineId}
                      </TableCell>
                      <TableCell className="text-sm" title={item.fsLine}>
                        <div className="max-w-[120px] truncate">
                          {item.fsLine}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {item.fsLineId}
                      </TableCell>
                      <TableCell className="text-sm">
                        <Badge
                          variant="outline"
                          className="border-white/10 text-xs text-white/80"
                        >
                          {item.type}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
